import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LucideCopy } from "lucide-react";
import { Modal } from "@/components/modals/Modal";

/**
 * Modal for duplicating an existing class.
 *
 * @param {object} props
 * @param {object|null} props.classItem - The class being duplicated, or null if closed
 * @param {() => void} props.onClose
 * @param {(classItem: object, name: string) => Promise<void>} props.onSubmit
 * @param {boolean} props.duplicating
 */
export function DuplicateClassModal({
  classItem,
  onClose,
  onSubmit,
  duplicating,
}) {
  const [name, setName] = useState("");

  useEffect(() => {
    if (classItem) {
      const defaultName = classItem.name
        ? `${classItem.name} - Copy`
        : [classItem.grade && `Grade ${classItem.grade}`, classItem.schoolYear]
            .filter(Boolean)
            .join(" · ") + " - Copy";
      setName(defaultName);
    }
  }, [classItem]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    await onSubmit(classItem, name.trim());
  }

  return (
    <Modal
      open={!!classItem}
      onClose={onClose}
      title="Duplicate Class"
      subtitle="Creates a copy with all student reports and their data."
      icon={<LucideCopy size={16} className="text-primary" />}
    >
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            New Class Name{" "}
            <span className="text-destructive/80 font-normal">*</span>
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="h-10"
          />
        </div>
        <div className="flex gap-2 pt-1">
          <Button
            type="submit"
            disabled={duplicating || !name.trim()}
            className="flex-1"
          >
            {duplicating ? "Duplicating..." : "Duplicate"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
